import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { ComponentProps, memo, useEffect, useMemo } from 'react';

import { Card, Skeleton, useRebrand } from '@growtherapy/sprout-ui';

import { DrawerRefsProvider } from '@grow-therapy-team/seedling-components';

import RebrandBackground from '../assets/provider-bg.svg?react';
import {
  Navbar,
  PageContainer,
  PageProviders,
  PushDrawerContainer,
  StageContainer,
  useDetectStaleVersionInterval,
} from '../components';
import { TrackingEvents, sendLoggingEvents } from '../events';
import { nonEssentialElementProps, useSessionFocus } from '../hooks';
import { useInactiveTabPerformanceOptimization } from '../hooks/useInactiveTabPerformanceOptimization';
import { useWaitingRoomTrackingEvent } from '../hooks/useWaitingRoomTrackingEvent';
import { EventTypeEnum } from '../segment/types';
import { isMediaShareModeAtom, useHandleDeviceChanges, useInitLocalAudioVideoContext } from '../session';
import { LeaveSessionConfirmationModal } from '../session/components/LeaveSessionConfirmationModal';
import { useNavigationGuard } from '../session/hooks/useNavigationGuard';
import { usePeriodicallySendParticipantState } from '../session/messages/usePeriodicallySendParticipantState';
import { SessionGrid } from '../session/session-grid/SessionGrid';
import { useNetworkQualityLogging } from '../session/useNetworkQualityLogging';
import { useResetVideoOnMobileNavigation } from '../session/useResetVideoOnMobileNavigation';
import { Theme } from '../state';
import { participantCountAtom, roomAtom } from '../video-sdk';
import { useUpdateTrackStatsInterval } from '../video-sdk/twilio';
import { HelpModalWrapper as HelpModal } from './HelpModal';
import { InSessionTimerWrapper as InSessionTimer } from './InSessionTimer';
import { ProviderAvatarWrapper as ProviderAvatar } from './ProviderAvatar';
import { ProviderWhiteboard } from './ProviderWhiteboard';
import { PushDrawerWrapper as PushDrawer } from './PushDrawer';
import { RemoteParticipant } from './RemoteParticipant';
import { SelfVideoThumbnail } from './SelfThumbnailVideo';
import { useObscuredProviderContentProps } from './a11y';
import { AvsCtaPillWrapper as AvsCtaPill } from './after-visit-summary/AvsCtaPill';
import { useInitProviderChatClient } from './chat';
import { DrawerRefKey } from './client-information/utils/types';
import { StartNewSessionConfirmationModalWrapper as StartNewSessionConfirmationModal } from './clients-drawer/StartNewSessionConfirmationModal';
import { PUSH_DRAWER_ARIA_CONTROLS_ID } from './constants';
import { ProviderSessionControlsWrapper as SessionControls } from './controls';
import { useOpenHelpModalCallback } from './controls/useOpenHelpModalCallback';
import { IntersessionSummaryConnected as IntersessionSummary } from './intersession-summary/IntersessionSummary';
import { useSendProviderParticipantStateCallback } from './messages/useSendProviderParticipantStateCallback';
import { ProviderRejoinModalWrapper } from './rejoin-session/ProviderRejoinModalWrapper';
import { usePromptFollowUpToast } from './schedule-appointment/usePromptFollowUpToast';
import { IntentionSettingWrapper as IntentionSetting } from './session-intention/IntentionSetting';
import { ProviderTranscriptionConsentModalConnected as ProviderTranscriptionConsentModal } from './session-recording/ProviderTranscriptionConsentModal';
import { TranscriptionPillWrapper } from './session-recording/TranscriptionPill';
import { ProviderDrawerState, drawerStateAtom, inSessionPatientInformationAtom, providerShortIdAtom } from './state';
import { useInitTwilioSyncClient } from './sync';
import { UpcomingAgendaWrapper as UpcomingAgenda } from './upcoming-agenda/UpcomingAgenda';
import { useAnnouncementToast } from './useAnnouncementToast';
import { useInitProviderGlobalDDLoggerContext } from './useInitProviderGlobalDDLoggerContext';
import { useInitProviderUserTracking } from './useInitProviderUserTracking';
import { useMaxDurationWarning } from './useMaxDurationWarning';
import { useMomentumPilotAccess } from './useMomentumPilotAccess';
import { useProviderRoomListeners } from './useProviderRoomListeners';
import { useRecordUnaccompaniedProviderInterval } from './useRecordUnaccompaniedProviderInterval';
import { useShowNotificationInFaviconAndTitle } from './useUpdateFaviconAndTitle';
import { useVisitorPresenceMap } from './useVisitorPresenceMap';

function Background() {
  const isRebrand = useRebrand();

  if (isRebrand) {
    return <div className='pointer-events-none absolute w-screen h-screen bg-surface-inverse' />;
  }
  return <RebrandBackground className='pointer-events-none bg-neutral-900 absolute w-screen h-screen' aria-hidden />;
}

function InSessionStageEffects() {
  const sendParticipantState = useSendProviderParticipantStateCallback();
  usePeriodicallySendParticipantState(sendParticipantState);
  return null;
}

export function MobileSelfVideoThumbnail() {
  return (
    <SelfVideoThumbnail
      className='self-center relative my-10 sm:hidden'
      shouldShowHideSelf={false}
      showExpandVideoToggle={false}
      showSetPositionMenu={false}
      videoClassName='min-h-[14rem] max-h-[18rem] min-w-[90vw] max-w-screen-sm'
    />
  );
}

export function DesktopSelfVideoThumbnail(props: Partial<ComponentProps<typeof SelfVideoThumbnail>>) {
  const inSession = !!useAtomValue(participantCountAtom);
  const baseClassName = 'absolute';
  const variableProps = inSession
    ? {
        className: `${baseClassName} bottom-14`,
        showExpandVideoToggle: false,
      }
    : {
        className: `${baseClassName} hidden sm:inline`,
        showExpandVideoToggle: true,
      };
  const isMediaShareMode = useAtomValue(isMediaShareModeAtom);

  if (isMediaShareMode) return null;

  return <SelfVideoThumbnail shouldShowHideSelf showSetPositionMenu {...variableProps} {...props} />;
}

export const Stage = memo(function Stage() {
  const inSession = !!useAtomValue(participantCountAtom);
  const isMediaShareMode = useAtomValue(isMediaShareModeAtom);
  const obscuredContentProps = useObscuredProviderContentProps();
  const providerShortId = useAtomValue(providerShortIdAtom);
  const showUpdatedIntersessionCardKey = 'show_updated_intersession_card';
  const { pilotAccess, loading } = useMomentumPilotAccess(providerShortId ?? '', showUpdatedIntersessionCardKey);

  const thumbnail = useMemo(
    () => (
      <SelfVideoThumbnail
        showExpandVideoToggle={false}
        shouldShowHideSelf={false}
        showSetPositionMenu={false}
        className={classNames({
          'flex-1 max-w-[15rem] min-w-[10rem] sm:min-w-[unset] max-h-[8.5rem] min-h-[7.875rem] w-auto h-full sm:mb-2':
            isMediaShareMode,
        })}
        videoClassName='max-w-[15rem] max-h-[8.5rem] min-w-[unset] w-auto h-full [&>video]:relative mb-2 mr-2 sm:mr-0 flex-1 border rounded-2xl border-neutral-800 rebrand:border-border-state-selected-emphasis'
      />
    ),
    [isMediaShareMode],
  );

  const getStageCardElement = (): JSX.Element => {
    if (loading) {
      return (
        <Card className='max-h-[80%] sm:w-[40rem] w-full rebrand:p-450 rebrand:rounded-425-450'>
          <Skeleton />
        </Card>
      );
    } else if (pilotAccess?.enabled) {
      return <IntersessionSummary />;
    }
    return <UpcomingAgenda />;
  };

  return (
    <StageContainer {...obscuredContentProps} className='@container' rounded>
      {inSession ? (
        <>
          <InSessionStageEffects />
          <SessionGrid
            remoteParticipantComponent={RemoteParticipant}
            thumbnail={thumbnail}
            whiteboardComponent={ProviderWhiteboard}
          />
          <IntentionSetting />
          <InSessionTimer />
          <DesktopSelfVideoThumbnail />
        </>
      ) : (
        <>
          <Background />
          <DesktopSelfVideoThumbnail />
          <div
            className='relative h-full w-full flex flex-col overflow-y-auto overflow-x-hidden p-8'
            // The main page content is a scrollable container that potentially
            // does not contain any interactive elements. The tabindex is added
            // to ensure that the container is focusable and can be scrolled
            // using the keyboard.
            // See:
            // - https://www.a11yproject.com/posts/how-to-use-the-tabindex-attribute/#scrollable-overflow-containers
            // - https://chromestatus.com/feature/5231964663578624
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
          >
            <ProviderAvatar />
            <MobileSelfVideoThumbnail />
            <div className='flex flex-col flex-grow items-center justify-center min-h-0'>
              {getStageCardElement()}
              <AvsCtaPill className='mt-6' />
            </div>
          </div>
        </>
      )}
    </StageContainer>
  );
});

function PageEffects() {
  const setDrawerState = useSetAtom(drawerStateAtom);
  useInitLocalAudioVideoContext();
  useHandleDeviceChanges();
  useInitTwilioSyncClient();
  useVisitorPresenceMap();
  useProviderRoomListeners();
  useRecordUnaccompaniedProviderInterval();
  useNetworkQualityLogging({
    onOpenSettings: () => setDrawerState(ProviderDrawerState.SETTINGS),
  });
  useSessionFocus();
  useInitProviderGlobalDDLoggerContext();
  useInitProviderUserTracking();
  useResetVideoOnMobileNavigation();
  useMaxDurationWarning();
  useDetectStaleVersionInterval(!useAtomValue(roomAtom));
  useShowNotificationInFaviconAndTitle();
  useInitProviderChatClient();
  useUpdateTrackStatsInterval();
  useAnnouncementToast();

  useInactiveTabPerformanceOptimization();
  const providerShortId = useAtomValue(providerShortIdAtom);
  useWaitingRoomTrackingEvent(EventTypeEnum.PROVIDER_WAITING_ROOM, providerShortId);

  return null;
}

export function Page() {
  const inSessionPatientInformation = useAtomValue(inSessionPatientInformationAtom);

  const providerShortId = useAtomValue(providerShortIdAtom);
  const inSession = !!useAtomValue(participantCountAtom);
  const obscuredContentProps = useObscuredProviderContentProps();

  usePromptFollowUpToast(providerShortId, inSessionPatientInformation);

  useEffect(
    function sendLoggingEventsOnPageLoad() {
      if (inSession) {
        // TODO migrate to segment
        sendLoggingEvents(TrackingEvents.PROVIDER_ENTER_SESSION_ROOM);
      }
    },
    [inSession],
  );

  const openHelpModal = useOpenHelpModalCallback();
  const { isBlocked, stay, leave } = useNavigationGuard(inSession);

  return (
    <>
      <PageEffects />
      <PageProviders theme={Theme.DARK}>
        <LeaveSessionConfirmationModal isOpen={isBlocked} onStay={stay} onLeave={leave} />
        <PageContainer>
          <ProviderRejoinModalWrapper />
          <HelpModal />
          <ProviderTranscriptionConsentModal />
          <Navbar
            {...obscuredContentProps}
            onClickNeedHelpButton={openHelpModal}
            TranscriptionPillComponent={TranscriptionPillWrapper}
            {...nonEssentialElementProps}
            className={classNames(nonEssentialElementProps.className, 'order-1')}
          />
          <SessionControls />
          <PushDrawerContainer
            // The push drawer must come _after_ the buttons that can trigger it
            // in the DOM. This is so that a keyboard user can tab forward from
            // the triggering button to the drawer. However, the drawer and
            // stage are meant to be visually positioned _before_ the control
            // buttons, so we reorder them within their flex container.
            className='order-2 sm:px-2'
          >
            <DrawerRefsProvider<DrawerRefKey>>
              <Stage />
              <PushDrawer id={PUSH_DRAWER_ARIA_CONTROLS_ID} />
            </DrawerRefsProvider>
          </PushDrawerContainer>
        </PageContainer>
        <StartNewSessionConfirmationModal />
      </PageProviders>
    </>
  );
}
